import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bot, ArrowUpRight, Users, ClipboardList, Bell, Zap, CalendarDays, Megaphone } from "lucide-react";

const StatCard = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="bg-surface border border-surface-3 p-5 relative overflow-hidden group hover:border-amber/50 transition-colors" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500">{label}</div>
        <div className="font-display text-5xl tracking-wide text-white mt-2 leading-none">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-2">{sub}</div>}
      </div>
      <Icon className="w-5 h-5 text-amber/70 group-hover:text-amber transition-colors" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-amber/30 group-hover:bg-amber transition-colors" />
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [broadcastList, setBroadcastList] = useState([]);
  const { user } = useAuth();
  const canBroadcast = user && (user.role === "head_coach" || user.role === "athletic_director");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setData(data);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!canBroadcast) return;
    (async () => {
      try {
        const { data } = await api.get("/roster");
        const emails = new Set();
        for (const p of (data?.players || [])) {
          if (p.guardian_email) emails.add(p.guardian_email.trim());
          if (p.player_email) emails.add(p.player_email.trim());
        }
        setBroadcastList([...emails].filter(Boolean));
      } catch (e) { console.error("Failed to load broadcast list (dashboard still works)", e); }
    })();
  }, [canBroadcast]);

  const openBroadcast = () => {
    if (broadcastList.length === 0) return;
    if (!window.confirm(
      `Broadcast to the entire program?\n\n` +
      `This opens your mail app with ${broadcastList.length} address${broadcastList.length === 1 ? "" : "es"} ` +
      `(players + guardians) on BCC. Nothing sends until you hit Send.`
    )) return;
    const progName = user?.program_name || "Our Program";
    const subject = `${progName} — Program Announcement`;
    const body = `Team Family,\n\n(Type your announcement here.)\n\n— Coach\nGridiron OS`;
    window.location.href = `mailto:?bcc=${encodeURIComponent(broadcastList.join(","))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="dashboard-page">
      <TopBar title="Command Center" subtitle={`${today} · ${data?.user?.title || "Coach"}`} />
      <div className="p-8 space-y-8">
        {err && <div className="text-sm text-red-400" data-testid="dashboard-error">{err}</div>}

        {/* Hero row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-surface border border-surface-3 relative overflow-hidden" data-testid="hero-block">
            <div className="p-8 relative z-10">
              <div className="text-[10px] font-condensed tracking-[0.3em] uppercase text-amber">This Week</div>
              <div className="font-display text-5xl tracking-wide text-white mt-2 leading-tight uppercase">
                Win Third Down.<br/>
                <span className="text-amber">Win The Fourth Quarter.</span>
              </div>
              <div className="hairline my-6 w-full" />
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Next Practice</div>
                  <div className="font-semibold mt-1">{data?.upcoming_practice?.title || "—"}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{data?.upcoming_practice?.date} · {data?.upcoming_practice?.start_time}</div>
                </div>
                <div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Attendance</div>
                  <div className="font-semibold mt-1">
                    <span className="text-amber font-display text-2xl">{data?.attendance_summary?.present || 0}</span>
                    <span className="text-slate-500">/{data?.attendance_summary?.expected || 0}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{data?.attendance_summary?.unexcused || 0} unexcused</div>
                </div>
                <div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Staff On Roster</div>
                  <div className="font-semibold mt-1">
                    <span className="text-amber font-display text-2xl">{data?.counts?.staff || 0}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">Full staff</div>
                </div>
              </div>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-20"
                 style={{background: "url('https://images.pexels.com/photos/1147455/pexels-photo-1147455.jpeg?auto=compress&cs=tinysrgb&w=800') center/cover"}} />
          </div>

          {/* AI Quick access */}
          <Link to="/ai-coach" className="bg-surface border border-surface-3 p-6 relative overflow-hidden group hover:border-amber transition-colors" data-testid="ai-quick-card">
            <div className="flex items-start justify-between">
              <Bot className="w-8 h-8 text-amber" />
              <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-amber transition-colors" />
            </div>
            <div className="mt-6">
              <div className="text-[10px] font-condensed tracking-[0.3em] uppercase text-amber">AI Coach</div>
              <div className="font-display text-3xl uppercase tracking-wide mt-2 leading-tight">
                Ask Your<br/>Program.
              </div>
              <p className="text-xs text-slate-400 mt-3">Trained on your mission, playbook, and standards. Not generic football.</p>
            </div>
            <div className="mt-6 space-y-2">
              {(data?.ai_suggestions || []).slice(0,3).map((s) => (
                <div key={s} className="text-xs text-slate-300 border-l-2 border-amber/50 pl-3 py-1">{s}</div>
              ))}
            </div>
          </Link>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Practices Logged" value={data?.counts?.practices ?? "—"} sub="This program" icon={ClipboardList} testid="stat-practices" />
          <StatCard label="Identity Sections" value={data?.counts?.identity_sections ?? "—"} sub="Program DNA" icon={Zap} testid="stat-identity" />
          <StatCard label="Staff Members" value={data?.counts?.staff ?? "—"} sub="Rostered coaches" icon={Users} testid="stat-staff" />
          <StatCard label="Announcements" value={(data?.announcements || []).length} sub="This week" icon={Bell} testid="stat-announcements" />
        </div>

        {/* Two column: schedule + announcements */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-surface border border-surface-3">
            <div className="px-6 py-4 border-b border-surface-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-amber" />
                <div className="font-condensed uppercase tracking-[0.25em] text-sm">Today's Schedule</div>
              </div>
              <Link to="/practice-planner" className="text-[10px] font-condensed uppercase tracking-widest text-amber hover:underline">Practices →</Link>
            </div>
            <div className="divide-y divide-surface-3">
              {(data?.today_schedule || []).map((s, i) => (
                <div key={`${s.time}-${s.title || s.label || i}`} className="px-6 py-4 flex items-center gap-6 hover:bg-surface-2 transition-colors" data-testid={`schedule-${i}`}>
                  <div className="font-display text-3xl text-amber w-20 tabular-nums">{s.time}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{s.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-surface-3">
            <div className="px-6 py-4 border-b border-surface-3 flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-sm">Announcements</div>
            </div>
            <div className="divide-y divide-surface-3">
              {(data?.announcements || []).map((a) => (
                <div key={a.id} className="px-6 py-4" data-testid={`announcement-${a.id}`}>
                  <div className="font-semibold text-sm">{a.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{a.body}</div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-2">— {a.author}</div>
                </div>
              ))}
              {(!data?.announcements || data.announcements.length === 0) && (
                <div className="px-6 py-8 text-sm text-slate-500 text-center">No announcements yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* Coach Broadcast — HC / AD only */}
        {canBroadcast && broadcastList.length > 0 && (
          <button
            onClick={openBroadcast}
            data-testid="dashboard-broadcast"
            className="w-full text-left border border-amber/40 bg-amber/5 hover:bg-amber/10 transition-colors p-5 flex items-center gap-5 group"
          >
            <div className="w-12 h-12 bg-amber flex items-center justify-center shrink-0">
              <Megaphone className="w-6 h-6 text-[#0B0D12]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-condensed tracking-[0.3em] uppercase text-amber">One-Tap Broadcast</div>
              <div className="font-display text-2xl uppercase tracking-wide text-white mt-1">
                Reach the Whole Program
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Draft one announcement — sends to {broadcastList.length} player and guardian address{broadcastList.length === 1 ? "" : "es"} on BCC.
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-amber shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        )}

        {/* Quick links */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { to: "/program-identity", label: "Program Identity", sub: "Mission & values" },
            { to: "/staff-manual", label: "Staff Manual", sub: "Coach playbook" },
            { to: "/player-handbook", label: "Player Handbook", sub: "Team standards" },
            { to: "/practice-planner", label: "Practice Planner", sub: "Build the card" },
          ].map(q => (
            <Link key={q.to} to={q.to} className="border border-surface-3 p-5 hover:border-amber hover:bg-surface transition-colors group" data-testid={`quick-${q.to.replace('/', '')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-condensed tracking-[0.28em] uppercase text-slate-500">Quick Link</div>
                  <div className="font-display text-2xl uppercase tracking-wide text-white mt-1">{q.label}</div>
                  <div className="text-xs text-slate-400 mt-1">{q.sub}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-amber transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
